import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import {
  api,
  type ProspectOrderCreate,
  type ProspectOrderDetail,
  type ProspectOrderUpdate,
} from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import TenantProfileFields, {
  profilePayload,
  profileValues,
  type TenantProfileValues,
} from "../../../components/tenant-onboarding/TenantProfileFields.tsx";
import { useTenant } from "../../../contexts/TenantContext.tsx";
import NewProspectsOnlyField from "../../../components/agent/NewProspectsOnlyField.tsx";
import AssigneePicker from "./AssigneePicker.tsx";
import { ORDER_STATUSES, STATUS_LABELS } from "./orderConstants.ts";

// Mounted only while open, so each opening starts from the order (or the tenant).
interface OrderFormDialogProps {
  onClose: () => void;
  orgId: string;
  /** Undefined = create mode. */
  order?: ProspectOrderDetail;
  onSaved: (order: ProspectOrderDetail) => void;
}

interface FormState {
  title: string;
  status: string;
  // The brief, under the tenant profile's own names.
  profile: TenantProfileValues;
  requestedCount: string;
  deadline: string;
  newProspectsOnly: boolean;
  assigneeUserIds: string[];
}

function stateFromOrder(order: ProspectOrderDetail): FormState {
  return {
    title: order.title,
    status: order.status,
    profile: profileValues(order),
    requestedCount: order.requested_count == null ? "" : String(order.requested_count),
    deadline: order.deadline ?? "",
    newProspectsOnly: order.new_prospects_only,
    assigneeUserIds: order.assignees.map((a) => a.user_id),
  };
}

const parseNum = (s: string): number | null => {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

export default function OrderFormDialog({
  onClose,
  orgId,
  order,
  onSaved,
}: OrderFormDialogProps) {
  const isEdit = !!order;
  const queryClient = useQueryClient();
  // New orders go to the globally selected tenant and start from its profile.
  const { selectedTenantId, selectedTenant } = useTenant();
  const [form, setForm] = useState<FormState>(() =>
    order
      ? stateFromOrder(order)
      : {
          title: "",
          status: "new",
          profile: profileValues(selectedTenant),
          requestedCount: "",
          deadline: "",
          newProspectsOnly: false,
          assigneeUserIds: [],
        },
  );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const mutation = useMutation({
    mutationFn: () => {
      const common = {
        title: form.title.trim(),
        status: form.status as ProspectOrderCreate["status"],
        ...profilePayload(form.profile),
        requested_count: parseNum(form.requestedCount),
        deadline: form.deadline || null,
        new_prospects_only: form.newProspectsOnly,
        assignee_user_ids: form.assigneeUserIds,
      };
      if (isEdit && order) {
        const payload: ProspectOrderUpdate = common;
        return api.updateProspectOrder(orgId, order.id, payload);
      }
      const payload: ProspectOrderCreate = { ...common, tenant_id: selectedTenantId! };
      return api.createProspectOrder(orgId, payload);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: keys.prospectOrder.all });
      toast.success(isEdit ? "Order updated" : "Order created");
      onSaved(saved);
      onClose();
    },
  });

  const canSubmit = form.title.trim().length > 0 && (isEdit || !!selectedTenantId);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "Edit order" : "New prospect order"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            fullWidth
            required
          />

          {isEdit ? (
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status}
                label="Status"
                onChange={(e) => set("status", e.target.value)}
              >
                {ORDER_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : selectedTenantId ? (
            <FormHelperText>
              Ordering for <strong>{selectedTenant?.name}</strong>. Switch tenant in the
              sidebar to order for another.
            </FormHelperText>
          ) : (
            <Alert severity="warning">Select a tenant in the sidebar first.</Alert>
          )}

          <TenantProfileFields
            values={form.profile}
            onChange={(profile) => set("profile", profile)}
            minRows={3}
          />
          <FormHelperText sx={{ mt: -1 }}>
            {isEdit
              ? "A blank field uses the tenant's company profile."
              : "Starts from the tenant's company profile. Edit it for this request; a blank field uses the profile."}
          </FormHelperText>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Requested count"
              type="number"
              value={form.requestedCount}
              onChange={(e) => set("requestedCount", e.target.value)}
              fullWidth
            />
            <TextField
              label="Deadline"
              type="date"
              value={form.deadline}
              onChange={(e) => set("deadline", e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>

          <NewProspectsOnlyField
            checked={form.newProspectsOnly}
            onChange={(checked) => set("newProspectsOnly", checked)}
            tenantName={(isEdit ? order?.tenant_name : selectedTenant?.name) ?? "this tenant"}
          />

          {!isEdit && (
            <FormHelperText>
              Saving runs the tenant's prospect-order workflows on this request (the
              prospecting agent, Slack notices, collection copies), as set up under the tenant's
              Workflows page.
            </FormHelperText>
          )}

          <AssigneePicker
            orgId={orgId}
            value={form.assigneeUserIds}
            onChange={(ids) => set("assigneeUserIds", ids)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={!canSubmit}
          loading={mutation.isPending}
        >
          {isEdit ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
